import { TiktokPostsTableRow } from '../../TiktokPostsTable/TiktokPostsTable';
import { InstagramPostsTableRow } from '../../../../hooks/posts/useProjectDetailsInstagramPosts';
import { YoutubePostsTableRow } from '../../../../hooks/posts/useProjectDetailsYoutubePosts';
import { CellContext } from '@tanstack/react-table';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import { getTableMetaHelper, Skeleton } from '@round/ui-kit';
import { Button } from '@round/whitelabel-ui';
import { useState } from 'react';
import { CampaignTableRow } from 'Modules/Plans/Project/ProjectDetails/components/ProjectCampaignsTable/ProjectCampaignsTable';
import { asMoneyWithoutZeros } from 'helpers';
import PostPaymentRequestModal from 'Modules/Plans/Project/ProjectDetails/Posts/PostPaymentRequestModal/PostPaymentRequestModal';
import useProjectDetails from 'Modules/Plans/Project/ProjectDetails/useProjectDetails';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import { mapFormValuesToPatchData } from 'Modules/Plans/Project/ProjectDetails/Posts/PostPaymentRequestModal/helpers';
import {
    useClientInfluencer,
    useClientInfluencerAccount,
    useClientProviderFeeConfigs,
    useInvalidatePosts,
} from '@round/creatorbase-helpers/api-hooks';
import { getProjectDetailsPostsParams } from '../../../../hooks/posts/getProjectDetailsPostsParams';

type Row = TiktokPostsTableRow | InstagramPostsTableRow | YoutubePostsTableRow;

type Context<TRow extends Row> = CellContext<TRow, TRow['cost']>;
type Meta = BasePostTableMeta<Row> & {
    campaign: CampaignTableRow;
};

const getTableMeta = getTableMetaHelper<Meta>();

const UpdateCostCell = <TRow extends Row>({ getValue, table, row: { original } }: Context<TRow>) => {
    const { isLoading, campaign, updatePost } = getTableMeta(table);
    const [isUpdateCostModalOpen, setIsUpdateCostModalOpen] = useState(false);

    const { team } = useProjectDetails();
    const clientId = team?.client_id;

    const { data: feeConfigs } = useClientProviderFeeConfigs({ client_id: clientId });

    // TODO: Refresh the specific post instead of the whole campaign's posts
    const invalidatePosts = useInvalidatePosts();

    const { clientInfluencer: clientInfluencerAccount, isLoading: isClientInfluencerAccountLoading } =
        useClientInfluencerAccount(original.client_influencer_account_id, {
            enabled: isUpdateCostModalOpen,
        });

    const { data: clientInfluencer, isLoading: isClientInfluencerLoading } = useClientInfluencer(
        clientInfluencerAccount?.client_influencer_id,
        { enabled: isUpdateCostModalOpen }
    );

    if (isLoading) {
        return <Skeleton />;
    }

    const cost = getValue();

    return (
        <>
            <Button appearance="ghost" type="button" onClick={() => setIsUpdateCostModalOpen(true)}>
                {typeof cost === 'number' ? asMoneyWithoutZeros(cost, campaign.currency) : '-'}
            </Button>

            <PostPaymentRequestModal
                currencyId={campaign.currency?.id}
                clientId={clientId}
                post={original}
                isOpen={isUpdateCostModalOpen}
                onClose={() => setIsUpdateCostModalOpen(false)}
                updatePost={async (patchData) => {
                    const response = await updatePost?.(
                        original.id,
                        mapFormValuesToPatchData(patchData, feeConfigs?.results)
                    );

                    if (response?.status === 200) {
                        return {
                            success: true,
                        };
                    }

                    if (response?.status === 400) {
                        return {
                            success: false,
                            errors: response?.data
                                ? mapApiErrorsToFormikErrors({
                                      baseCost: response.data.base_cost,
                                      currencyId: response.data.currency_id,
                                      provider: response.data.fee_config_id,
                                      notes: response.data.notes,
                                      fileUpload: response.data.payment_request_file_upload,
                                      paypalEmail: response.data?.paypal_email_address,
                                  })
                                : undefined,
                        };
                    }

                    return {
                        success: false,
                    };
                }}
                onPaymentRequestUpdated={() => invalidatePosts(getProjectDetailsPostsParams(campaign.id))}
                defaultValues={{
                    paypalEmail: clientInfluencer?.paypal_email ?? undefined,
                }}
                areDefaultValuesLoading={isClientInfluencerLoading || isClientInfluencerAccountLoading}
            />
        </>
    );
};

export default UpdateCostCell;
